import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactElement,
} from 'react';
import type { PropertySummary } from '@/lib/types/property';

interface PropertySelectionContextValue {
  selectedMap: Map<string, PropertySummary>;
  selectedCount: number;
  selectedProperties: PropertySummary[];
  isSelected: (propertyId: string) => boolean;
  toggleProperty: (property: PropertySummary, force?: boolean) => void;
  selectProperties: (properties: readonly PropertySummary[]) => void;
  clearSelection: () => void;
}

const PropertySelectionContext = createContext<PropertySelectionContextValue | null>(null);

export interface PropertySelectionProviderProps extends PropsWithChildren {
  enabled: boolean;
  scopeKey?: string;
}

export function PropertySelectionProvider({
  enabled,
  scopeKey = 'default',
  children,
}: PropertySelectionProviderProps): ReactElement {
  const [selectedMap, setSelectedMap] = useState<Map<string, PropertySummary>>(new Map());
  const scopeRef = useRef(scopeKey);

  useEffect(() => {
    if (scopeRef.current !== scopeKey) {
      scopeRef.current = scopeKey;
      setSelectedMap(new Map());
    }
  }, [scopeKey]);

  useEffect(() => {
    if (!enabled) {
      setSelectedMap(new Map());
    }
  }, [enabled]);

  const toggleProperty = useCallback((property: PropertySummary, force?: boolean) => {
    setSelectedMap((current) => {
      const next = new Map(current);
      const shouldSelect = force ?? !next.has(property.id);
      if (shouldSelect) {
        next.set(property.id, property);
      } else {
        next.delete(property.id);
      }
      return next;
    });
  }, []);

  const selectProperties = useCallback((properties: readonly PropertySummary[]) => {
    setSelectedMap(() => {
      const next = new Map<string, PropertySummary>();
      properties.forEach((property) => {
        next.set(property.id, property);
      });
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedMap(new Map());
  }, []);

  const contextValue = useMemo<PropertySelectionContextValue>(() => {
    const selectedProperties = Array.from(selectedMap.values());
    return {
      selectedMap,
      selectedCount: selectedMap.size,
      selectedProperties,
      isSelected: (propertyId: string) => selectedMap.has(propertyId),
      toggleProperty,
      selectProperties,
      clearSelection,
    };
  }, [clearSelection, selectProperties, selectedMap, toggleProperty]);

  return (
    <PropertySelectionContext.Provider value={contextValue}>
      {children}
    </PropertySelectionContext.Provider>
  );
}

export function usePropertySelection(): PropertySelectionContextValue {
  const context = useContext(PropertySelectionContext);
  if (!context) {
    throw new Error('usePropertySelection must be used within PropertySelectionProvider');
  }
  return context;
}
